import React, { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { regions } from '../../config/pricingConfig';

function PropertiesPanel({ selectedNode, onUpdate }) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [cidr, setCidr] = useState('');
  const [resourceGroupName, setResourceGroupName] = useState('');
  const [sku, setSku] = useState('');
  const [diskType, setDiskType] = useState('');
  const [diskSize, setDiskSize] = useState('');
  const [accountTier, setAccountTier] = useState('');
  const [replicationType, setReplicationType] = useState('');
  const [securityRules, setSecurityRules] = useState('');
  const [routes, setRoutes] = useState('');
  const [kvSku, setKvSku] = useState('');
  const [appOsType, setAppOsType] = useState('');
  const [appRuntime, setAppRuntime] = useState('');
  const [lbSku, setLbSku] = useState('');
  const [lbType, setLbType] = useState('');

  useEffect(() => {
    if (selectedNode) {
      setName(selectedNode.data.name || '');
      setRegion(selectedNode.data.region || '');
      setCidr(selectedNode.data.cidr || '');
      setResourceGroupName(selectedNode.data.resourceGroupName || '');
      setSku(selectedNode.data.sku || '');
      setDiskType(selectedNode.data.diskType || '');
      setDiskSize(selectedNode.data.diskSize || '');
      setAccountTier(selectedNode.data.accountTier || '');
      setReplicationType(selectedNode.data.replicationType || '');
      setSecurityRules(selectedNode.data.securityRules || '');
      setRoutes(selectedNode.data.routes || '');
      setKvSku(selectedNode.data.kvSku || '');
      setAppOsType(selectedNode.data.appOsType || '');
      setAppRuntime(selectedNode.data.appRuntime || '');
      setLbSku(selectedNode.data.lbSku || '');
      setLbType(selectedNode.data.lbType || '');
    }
  }, [selectedNode]);

  if (!selectedNode) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
        <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-3">
          <svg
            className="h-5 w-5 text-gray-400 dark:text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No node selected</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Click a node on the canvas to edit its properties
        </p>
      </div>
    );
  }

  const nodeType = selectedNode.data.type;

  function handleSubmit(e) {
    e.preventDefault();
    const values = { name, type: nodeType };
    if (nodeType === 'resource-group' || nodeType === 'vnet') {
      values.region = region;
    }
    if (nodeType === 'vnet' || nodeType === 'subnet') {
      values.cidr = cidr;
    }
    if (nodeType === 'vnet' || nodeType === 'subnet' || nodeType === 'virtual-machine' || nodeType === 'managed-disk' || nodeType === 'storage-account' || nodeType === 'nsg' || nodeType === 'route-table' || nodeType === 'key-vault' || nodeType === 'app-service' || nodeType === 'load-balancer') {
      values.resourceGroupName = resourceGroupName;
    }
    if (nodeType === 'virtual-machine') {
      values.sku = sku;
    }
    if (nodeType === 'managed-disk') {
      values.diskType = diskType;
      values.diskSize = parseInt(diskSize, 10) || 128;
    }
    if (nodeType === 'storage-account') {
      values.accountTier = accountTier;
      values.replicationType = replicationType;
    }
    if (nodeType === 'nsg') {
      values.securityRules = securityRules;
    }
    if (nodeType === 'route-table') {
      values.routes = routes;
    }
    if (nodeType === 'key-vault') {
      values.kvSku = kvSku;
    }
    if (nodeType === 'app-service') {
      values.appOsType = appOsType;
      values.appRuntime = appRuntime;
    }
    if (nodeType === 'load-balancer') {
      values.lbSku = lbSku;
      values.lbType = lbType;
    }
    onUpdate(selectedNode.id, values);
  }

  return (
    <div className="flex flex-col gap-4 p-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Properties
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
          {nodeType?.replace('-', ' ')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="prop-name" className="text-xs">
            Name
          </label>
          <Input
            id="prop-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter name"
            className="h-8 text-sm"
          />
        </div>

        {(nodeType === 'resource-group' || nodeType === 'vnet') && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-region" className="text-xs">
              Region
            </label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger id="prop-region" className="h-8 text-sm">
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((r) => (
                  <SelectItem key={r.value} value={r.value} className="text-sm">
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {(nodeType === 'vnet' || nodeType === 'subnet') && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-cidr" className="text-xs">
              CIDR Block
            </label>
            <Input
              id="prop-cidr"
              value={cidr}
              onChange={(e) => setCidr(e.target.value)}
              placeholder="e.g. 10.0.0.0/16"
              className="h-8 font-mono text-sm"
            />
          </div>
        )}

        {(nodeType === 'vnet' || nodeType === 'subnet') && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-rg" className="text-xs">
              Resource Group
            </label>
            <Input
              id="prop-rg"
              value={resourceGroupName}
              onChange={(e) => setResourceGroupName(e.target.value)}
              placeholder="Resource group name"
              className="h-8 text-sm"
            />
          </div>
        )}

        {(nodeType === 'virtual-machine' || nodeType === 'managed-disk' || nodeType === 'storage-account' || nodeType === 'nsg' || nodeType === 'route-table' || nodeType === 'key-vault' || nodeType === 'app-service' || nodeType === 'load-balancer') && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-rg2" className="text-xs">
              Resource Group
            </label>
            <Input
              id="prop-rg2"
              value={resourceGroupName}
              onChange={(e) => setResourceGroupName(e.target.value)}
              placeholder="Resource group name"
              className="h-8 text-sm"
            />
          </div>
        )}

        {nodeType === 'virtual-machine' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-sku" className="text-xs">
              VM SKU
            </label>
            <Input
              id="prop-sku"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="e.g. Standard_B2s"
              className="h-8 font-mono text-sm"
            />
          </div>
        )}

        {nodeType === 'managed-disk' && (
          <>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-disktype" className="text-xs">
                Disk Type
              </label>
              <Select value={diskType} onValueChange={setDiskType}>
                <SelectTrigger id="prop-disktype" className="h-8 text-sm">
                  <SelectValue placeholder="Select disk type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Premium_LRS" className="text-sm">Premium SSD (Premium_LRS)</SelectItem>
                  <SelectItem value="StandardSSD_LRS" className="text-sm">Standard SSD (StandardSSD_LRS)</SelectItem>
                  <SelectItem value="Standard_LRS" className="text-sm">Standard HDD (Standard_LRS)</SelectItem>
                  <SelectItem value="Premium_ZRS" className="text-sm">Premium Zone-Redundant (Premium_ZRS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-disksize" className="text-xs">
                Disk Size (GB)
              </label>
              <Input
                id="prop-disksize"
                type="number"
                min="1"
                value={diskSize}
                onChange={(e) => setDiskSize(e.target.value)}
                placeholder="128"
                className="h-8 text-sm"
              />
            </div>
          </>
        )}

        {nodeType === 'storage-account' && (
          <>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-tier" className="text-xs">
                Account Tier
              </label>
              <Select value={accountTier} onValueChange={setAccountTier}>
                <SelectTrigger id="prop-tier" className="h-8 text-sm">
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard" className="text-sm">Standard</SelectItem>
                  <SelectItem value="Premium" className="text-sm">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-repl" className="text-xs">
                Replication
              </label>
              <Select value={replicationType} onValueChange={setReplicationType}>
                <SelectTrigger id="prop-repl" className="h-8 text-sm">
                  <SelectValue placeholder="Select replication" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LRS" className="text-sm">LRS (Locally Redundant)</SelectItem>
                  <SelectItem value="GRS" className="text-sm">GRS (Geo Redundant)</SelectItem>
                  <SelectItem value="RAGRS" className="text-sm">RAGRS (Read-Access Geo)</SelectItem>
                  <SelectItem value="ZRS" className="text-sm">ZRS (Zone Redundant)</SelectItem>
                  <SelectItem value="GZRS" className="text-sm">GZRS (Geo Zone Redundant)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {nodeType === 'nsg' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-rules" className="text-xs">
              Security Rules (one per line)
            </label>
            <textarea
              id="prop-rules"
              value={securityRules}
              onChange={(e) => setSecurityRules(e.target.value)}
              placeholder="name|priority|direction|action|srcAddr|srcPort|dstAddr|dstPort|protocol&#10;Allow-SSH|100|Inbound|Allow|*|*|*|*|22|TCP"
              className="h-20 text-xs font-mono p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <p className="text-xs text-gray-400">Format: name|priority|direction|action|src|srcPort|dst|dstPort|protocol</p>
          </div>
        )}

        {nodeType === 'route-table' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-routes" className="text-xs">
              Routes (one per line)
            </label>
            <textarea
              id="prop-routes"
              value={routes}
              onChange={(e) => setRoutes(e.target.value)}
              placeholder="name|addressPrefix|nextHopType&#10;to-internet|0.0.0.0/0|Internet"
              className="h-20 text-xs font-mono p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <p className="text-xs text-gray-400">Format: name|addressPrefix|nextHopType</p>
          </div>
        )}

        {nodeType === 'key-vault' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prop-kvsku" className="text-xs">
              Key Vault SKU
            </label>
            <Select value={kvSku} onValueChange={setKvSku}>
              <SelectTrigger id="prop-kvsku" className="h-8 text-sm">
                <SelectValue placeholder="Select SKU" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard" className="text-sm">Standard</SelectItem>
                <SelectItem value="premium" className="text-sm">Premium (HSM)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {nodeType === 'app-service' && (
          <>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-os" className="text-xs">
                OS Type
              </label>
              <Select value={appOsType} onValueChange={setAppOsType}>
                <SelectTrigger id="prop-os" className="h-8 text-sm">
                  <SelectValue placeholder="Select OS" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Linux" className="text-sm">Linux</SelectItem>
                  <SelectItem value="Windows" className="text-sm">Windows</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-runtime" className="text-xs">
                Runtime Stack
              </label>
              <Input
                id="prop-runtime"
                value={appRuntime}
                onChange={(e) => setAppRuntime(e.target.value)}
                placeholder="e.g. NODE:18-lts, PYTHON:3.10, DOTNET:7.0"
                className="h-8 font-mono text-sm"
              />
            </div>
          </>
        )}

        {nodeType === 'load-balancer' && (
          <>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-lbsku" className="text-xs">
                LB SKU
              </label>
              <Select value={lbSku} onValueChange={setLbSku}>
                <SelectTrigger id="prop-lbsku" className="h-8 text-sm">
                  <SelectValue placeholder="Select SKU" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Basic" className="text-sm">Basic</SelectItem>
                  <SelectItem value="Standard" className="text-sm">Standard</SelectItem>
                  <SelectItem value="Gateway" className="text-sm">Gateway (WAF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="prop-lbtype" className="text-xs">
                Type
              </label>
              <Select value={lbType} onValueChange={setLbType}>
                <SelectTrigger id="prop-lbtype" className="h-8 text-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Public" className="text-sm">Public (Frontend IP)</SelectItem>
                  <SelectItem value="Internal" className="text-sm">Internal (Private IP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <Button type="submit" className="mt-1 w-full py-1 text-sm">
          Apply
        </Button>
      </form>
    </div>
  );
}

export default PropertiesPanel;
